import { Link, useLocalSearchParams } from "expo-router";
import { useEffect, useState } from "react";
import { Text, useWindowDimensions, View } from "react-native";

import { TableDisplayBoard } from "@/components/table-display/TableDisplayBoard";
import { usePublicTableDisplay } from "@/hooks/useTableDisplay";
import { useWakeLock } from "@/hooks/useWakeLock";
import { normalizeDisplayCode, ROTATE_MS } from "@/lib/shows/tableDisplay";
import { palette } from "@/theme/palette";

/**
 * The Table display board (`/t/<code>`): a Vendor's live, in-stock inventory on a monitor, with no
 * input. It polls the public board, rotates the featured card every few seconds, holds a screen
 * wake lock, and shows Reconnecting instead of any price when the connection drops.
 */
export default function TableDisplayScreen() {
  const params = useLocalSearchParams<{ code?: string; }>();
  const code = normalizeDisplayCode(params.code ?? "");
  const { width, height } = useWindowDimensions();
  const { board, status } = usePublicTableDisplay(code);
  const [tick, setTick] = useState(0);
  useWakeLock(true);
  useEffect(() => {
    const timer = setInterval(() => setTick((n) => n + 1), ROTATE_MS);
    return () => clearInterval(timer);
  }, []);

  if (!code || status === "gone") {
    return (
      <View
        style={{
          flex: 1,
          alignItems: "center",
          justifyContent: "center",
          gap: 16,
          padding: 32,
          backgroundColor: palette.dark.bg,
        }}
      >
        <Text
          accessibilityRole="header"
          style={{
            color: palette.dark.foreground,
            fontFamily: "BricolageGrotesque_700Bold",
            fontSize: 40,
          }}
        >
          This display code is not active
        </Text>
        <Text
          style={{
            color: palette.dark.mutedForeground,
            fontFamily: "BricolageGrotesque_400Regular",
            fontSize: 24,
          }}
        >
          Ask the shop for the current code.
        </Text>
        <Link
          href="/t"
          style={{
            color: palette.dark.primary,
            fontFamily: "BricolageGrotesque_600SemiBold",
            fontSize: 24,
          }}
        >
          Enter a code
        </Link>
      </View>
    );
  }
  return (
    <View style={{ flex: 1, backgroundColor: palette.dark.bg }}>
      <TableDisplayBoard
        board={board}
        width={width}
        height={height}
        tick={tick}
        reconnecting={status !== "live"}
      />
    </View>
  );
}
